import { useState, useEffect } from "react";
import { useAlert } from "../../Provider/Alert/AlertContext";
import { useTranslation } from "react-i18next";
import { getData, putData, postData } from "../../Services/apiService";
import { useNavigate } from "react-router-dom";

// Normaliza uma string para comparação: garante string, remove espaços e deixa em lowercase
const normalizeString = (s) => (s || "").toString().trim().toLowerCase();

export function useClientForm({ id = null }) {
    const { addAlert } = useAlert();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        is_active: true,
        legal_entity: true,
        address: {
            zip_code: "",
            state: {},
            city: {},
        },
        contact: {},
    });

    useEffect(() => {
        console.log("useClientForm: useEffect on mount");

        // Se tiver id válido, buscar dados do cliente
        if (id && !isNaN(id)) {
            setLoading(true);
            getClient();
        }

        getListStates();
        // eslint-disable-next-line
    }, [id]);

    const getClient = async () => {
        try {
            const response = await getData(`/client/${id}`);
            if (response.status === 200) {
                setFormData(response.body.data);
                setLoading(false);
                return;
            }

            addAlert({
                type: "danger",
                title: t("client.alert.title.error"),
                message: t("client.alert.message.error"),
            });
        } catch (error) {
            addAlert({
                type: "danger",
                title: t("client.alert.title.error"),
                message: t("client.alert.message.error"),
            });
        } finally {
            setLoading(false);
        }
    };

    const [states, setStates] = useState([]);
    const getListStates = async () => {
        try {
            const getStates = await getData("/state", {});

            console.log("States response:", getStates);

            if (getStates.status === 200) {
                setStates(getStates.body);
                return;
            }

            if (getStates.status !== 404) {
                addAlert(
                    "danger",
                    t("error.state.title"),
                    t("error.state.message")
                );
            }
        } catch (error) {
            console.error(error);
        }
    };

    const [cities, setCities] = useState([]);
    const getListCities = async (id_state) => {
        try {
            const getCities = await getData(`/state/${id_state}/city`, {});

            if (getCities.status === 200) {
                setCities(getCities.body);
                return;
            }

            if (getCities.status !== 404) {
                addAlert(
                    "danger",
                    t("error.city.title"),
                    t("error.city.message")
                );
            }
        } catch (error) {
            console.error(error);
        }
    };

    const saveClient = async () => {
        setLoading(true);

        console.log("Saving client with data:", formData);

        try {
            let response;
            if (id && !isNaN(id)) {
                response = await putData("/client", id, formData);
            } else {
                response = await postData("/client", formData);
            }

            console.log("Save client response:", response);

            if (response.status === 200 || response.status === 201) {
                addAlert(
                    "success",
                    t("client.form.success.title"),
                    t("client.form.success.message")
                );

                // Se for criação, redirecionar para edição do novo cliente
                if (!id) {
                    navigate(`/client/${response.body.data.id}`);
                }

                setLoading(false);
                return;
            }

            addAlert(
                "danger",
                t("client.form.error.title"),
                t("client.form.error.message")
            );
            setLoading(false);
        } catch (error) {
            console.error("Error saving client:", error);
            addAlert(
                "danger",
                t("client.form.error.title"),
                t("client.form.error.message")
            );
        } finally {
            setLoading(false);
        }
    };

    const setClientFormCnpj = async (newClientData) => {
        // Buscar estado e cidades
        const state = states.find((s) => s.uf === newClientData.uf);
        if (!state) {
            addAlert(
                "danger",
                t("error.state.title"),
                t("error.state.message")
            );
            return;
        }

        // Buscar cidades do estado
        let city = null;
        const getCities = await getData(`/state/${state.id}/city`, {});

        if (getCities.status === 200) {
            setCities(getCities.body); // Atualiza a lista de cidades

            // Atribui a cidade correta com base no nome
            city = getCities.body.find(
                (c) =>
                    normalizeString(c.name) ===
                    normalizeString(newClientData.municipio)
            );
        }

        console.log("City found:", city);

        // Atualiza o formData com os dados do CNPJ
        setFormData((prevData) => ({
            name: newClientData.razao_social || prevData.name,
            last_name: newClientData.nome_fantasia || "*",
            legal_entity: true,
            cpf_cnpj: prevData.cpf_cnpj,
            is_active: prevData.is_active,
            ie: prevData.ie,
            address: {
                zip_code: newClientData.cep || prevData.address.zip_code,
                state: state,
                city: city || prevData.address.city,
                street: newClientData.logradouro || prevData.address.street,
                number: newClientData.numero || prevData.address.number,
                complement:
                    newClientData.complemento || prevData.address.complement,
                neighborhood:
                    newClientData.bairro || prevData.address.neighborhood,
            },
            contact: {
                phone: newClientData.ddd_telefone_1 || prevData.contact.phone,
                mobile: newClientData.ddd_telefone_2 || prevData.contact.mobile,
                email: newClientData.email || prevData.contact.email,
                other: prevData.contact.other || "",
            },
        }));
    };

    /**
     * Sets the address fields in the form data based on the provided CEP (postal code) data.
     * Fetches the corresponding state and city information, updates the cities list,
     * and handles errors if the state or city cannot be found.
     *
     * @async
     * @function setAddressFromCep
     * @param {Object} cepData - The data retrieved from the CEP lookup.
     * @param {string} cepData.uf - The state abbreviation from the CEP data.
     * @param {string} cepData.logradouro - The street name from the CEP data.
     * @param {string} cepData.bairro - The neighborhood from the CEP data.
     * @param {string} cepData.localidade - The city name from the CEP data.
     * @returns {Promise<void>} Resolves when the address is set or an error is handled.
     */
    const setAddressFromCep = async (cepData) => {
        try {
            const id_state = states.find((s) => s.uf === cepData.uf)?.id;
            if (!id_state) {
                addAlert(
                    "danger",
                    t("error.state.title"),
                    t("error.state.message")
                );
                return;
            }

            const getCities = await getData(`/state/${id_state}/city`, {});

            if (getCities.status === 200) {
                setCities(getCities.body);

                setFormData((prevData) => ({
                    ...prevData,
                    address: {
                        ...prevData.address,
                        street: cepData.logradouro,
                        neighborhood: cepData.bairro,
                        state: { id: id_state },
                        city: {
                            id:
                                getCities.body.find(
                                    (c) => c.name === cepData.localidade
                                )?.id || null,
                        },
                    },
                }));
                return;
            }

            if (getCities.status !== 404) {
                addAlert(
                    "danger",
                    t("error.city.title"),
                    t("error.city.message")
                );
                return;
            }
        } catch (error) {
            console.error(error);
        }
    };

    return {
        isEditing: !!id && !isNaN(id),
        loading,
        formData,
        setFormData,
        states,
        getListCities,
        cities,
        saveClient,
        setAddressFromCep,
        setClientFormCnpj,
    };
}
